export class CrawlService {
  private static instance: CrawlService;

  private static measureCtx: CanvasRenderingContext2D | null = null;

  private listeners: Set<(time: number) => void>;

  private startTime: number = 0;

  private rafId: number | null = null;

  public static readonly REFERENCE_WIDTH = 1920;

  private static readonly SCALE_FACTOR = 2.01;

  public static readonly SPEEDS = {
    slow: 0.125,

    medium: 0.25,

    fast: 0.5,

    insane: 1,
  };

  private constructor() {
    this.listeners = new Set();

    this.startTime = performance.now();

    // Ticker starts lazily on the first subscribe — see subscribe()/startTicker().
  }

  public static getInstance(): CrawlService {
    if (!CrawlService.instance) {
      CrawlService.instance = new CrawlService();
    }

    return CrawlService.instance;
  }

  public reset(): void {
    this.startTime = performance.now();
  }

  /**
   * Computes textRatio deterministically using canvas measureText.
   * Both Pixi and React use this so cycleLen is identical — no progressive drift.
   */

  public static computeTextRatio(
    text: string,

    fontFamily: string,

    fontSize: string,

    bold = false,
  ): number {
    if (!CrawlService.measureCtx) {
      const canvas = document.createElement("canvas");

      CrawlService.measureCtx = canvas.getContext("2d")!;
    }

    const size = parseFloat(fontSize) * CrawlService.SCALE_FACTOR;

    CrawlService.measureCtx.font = `${
      bold ? "bold" : "normal"
    } ${size}px ${fontFamily}`;

    const width = CrawlService.measureCtx.measureText(text).width;

    return width / CrawlService.REFERENCE_WIDTH;
  }

  public subscribe(callback: (time: number) => void): void {
    this.listeners.add(callback);

    // (Re)arm the rAF loop if it was idle (no listeners) — keeps `elapsed`
    // monotonic across subscribe gaps since startTime is never reset here.
    if (this.rafId === null) {
      this.startTicker();
    }
  }

  public unsubscribe(callback: (time: number) => void): void {
    this.listeners.delete(callback);

    if (this.listeners.size === 0) {
      this.stop();
    }
  }

  public stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);

      this.rafId = null;
    }
  }

  private startTicker(): void {
    const tick = (now: number) => {
      // Bail out of the loop when nobody is listening — avoids a permanent
      // 60fps wake-up on the main thread once every crawl widget unmounts.
      if (this.listeners.size === 0) {
        this.rafId = null;

        return;
      }

      const elapsed = now - this.startTime;

      for (const listener of this.listeners) {
        listener(elapsed);
      }

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }
}
